/**
 * Sports Mapping Utility
 * 
 * Central dictionary for mapping between different sport identifier formats used across the app.
 * This ensures consistency between The Odds API format, display names, and filtering.
 */

// Map from full sport_key (The Odds API format) to short display name
export const SPORT_KEY_TO_SHORT = {
  'americanfootball_nfl': 'NFL',
  'americanfootball_nfl_preseason': 'NFL Preseason',
  'americanfootball_ncaaf': 'NCAAF',
  'basketball_nba': 'NBA',
  'basketball_ncaab': 'NCAAB',
  'basketball_wnba': 'WNBA',
  'baseball_mlb': 'MLB',
  'icehockey_nhl': 'NHL',
  'soccer_epl': 'EPL',
  'soccer_usa_mls': 'MLS',
  'soccer_uefa_champs_league': 'UEFA CL',
  'soccer_fifa_world_cup': 'World Cup',
  'soccer_fifa_world_cup_winner': 'World Cup Winner',
  'tennis_wta': 'WTA',
  'tennis_atp': 'ATP',
  'golf_pga_championship': 'PGA',
  'mma_mixed_martial_arts': 'MMA',
  'motorsport_nascar': 'NASCAR',
};

// Map from short name to full sport_key
export const SHORT_TO_SPORT_KEY = {
  'NFL': 'americanfootball_nfl',
  'NFL Preseason': 'americanfootball_nfl_preseason',
  'NCAAF': 'americanfootball_ncaaf',
  'NBA': 'basketball_nba',
  'NCAAB': 'basketball_ncaab',
  'WNBA': 'basketball_wnba',
  'MLB': 'baseball_mlb',
  'NHL': 'icehockey_nhl',
  'EPL': 'soccer_epl',
  'MLS': 'soccer_usa_mls',
  'UEFA CL': 'soccer_uefa_champs_league',
  'World Cup': 'soccer_fifa_world_cup',
  'World Cup Winner': 'soccer_fifa_world_cup_winner',
  'WTA': 'tennis_wta',
  'ATP': 'tennis_atp',
  'PGA': 'golf_pga_championship',
  'MMA': 'mma_mixed_martial_arts',
  'NASCAR': 'motorsport_nascar',
};

// Tab identifier to full sport_key mapping (for filtering)
export const TAB_TO_SPORT_KEY = {
  'all': null, // null means show all
  'nfl': 'americanfootball_nfl',
  'ncaaf': 'americanfootball_ncaaf',
  'nba': 'basketball_nba',
  'ncaab': 'basketball_ncaab',
  'wnba': 'basketball_wnba',
  'mlb': 'baseball_mlb',
  'nhl': 'icehockey_nhl',
  'epl': 'soccer_epl',
  'mls': 'soccer_usa_mls',
  'mma': 'mma_mixed_martial_arts',
  'uefa': 'soccer_uefa_champs_league',
  'worldcup': 'soccer_fifa_world_cup',
  'worldcupwinner': 'soccer_fifa_world_cup_winner',
};

/**
 * Convert a full sport_key to short display name
 * @param {string} sportKey - Full sport key (e.g., 'americanfootball_nfl')
 * @returns {string} Short display name (e.g., 'NFL')
 */
export function sportKeyToShort(sportKey) {
  if (!sportKey) return '';
  return SPORT_KEY_TO_SHORT[sportKey] || sportKey.toUpperCase();
}

/**
 * Convert a short name to full sport_key
 * @param {string} shortName - Short sport name (e.g., 'NFL')
 * @returns {string} Full sport key (e.g., 'americanfootball_nfl')
 */
export function shortToSportKey(shortName) {
  if (!shortName) return '';
  return SHORT_TO_SPORT_KEY[shortName.toUpperCase()] || '';
}

/**
 * Get the full sport_key for a tab identifier
 * @param {string} tabValue - Tab identifier (e.g., 'nfl', 'nba')
 * @returns {string|null} Full sport key or null for 'all'
 */
export function tabToSportKey(tabValue) {
  if (!tabValue) return null;
  return TAB_TO_SPORT_KEY[tabValue.toLowerCase()] || null;
}

/**
 * Check if a pick's sport matches a tab filter
 * @param {object} pick - Pick object with sport property
 * @param {string} tabValue - Tab identifier (e.g., 'nfl', 'nba', 'all')
 * @returns {boolean} True if pick matches the filter
 */
export function pickMatchesTab(pick, tabValue) {
  if (!pick || !pick.sport) return false;
  if (tabValue === 'all') return true;
  
  const sportKey = tabToSportKey(tabValue);
  if (!sportKey) return false;
  
  return pick.sport.toLowerCase() === sportKey.toLowerCase();
}

/**
 * Extract sport category from full sport key
 * (e.g., 'americanfootball_nfl' -> 'americanfootball')
 * @param {string} sportKey - Full sport key
 * @returns {string} Sport category
 */
export function getSportCategory(sportKey) {
  if (!sportKey) return '';
  const parts = sportKey.split('_');
  return parts[0] || '';
}

/**
 * Get all available sport keys
 * @returns {string[]} Array of all sport keys
 */
export function getAllSportKeys() {
  return Object.keys(SPORT_KEY_TO_SHORT);
}

/**
 * Get all available short names
 * @returns {string[]} Array of all short names
 */
export function getAllShortNames() {
  return Object.keys(SHORT_TO_SPORT_KEY);
}
